import { useEffect, useRef, useState } from "react";

interface TimesheetHourCellProps {
  hours: number | null;
  readOnly: boolean;
  onSave: (hours: number) => Promise<void>;
}

function roundToQuarter(value: number): number {
  return Math.round(value * 4) / 4;
}

export default function TimesheetHourCell({ hours, readOnly, onSave }: TimesheetHourCellProps) {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.select();
    }
  }, [editing]);

  const handleClick = () => {
    if (readOnly) return;
    setInputValue(hours != null && hours > 0 ? String(hours) : "");
    setEditing(true);
  };

  const handleBlur = async () => {
    setEditing(false);
    const raw = parseFloat(inputValue);
    if (isNaN(raw) || raw < 0) return;
    const rounded = roundToQuarter(Math.min(raw, 24));
    if (rounded === (hours ?? 0)) return;
    setSaving(true);
    try {
      await onSave(rounded);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") inputRef.current?.blur();
    if (e.key === "Escape") {
      setEditing(false);
      setInputValue("");
    }
  };

  const displayValue = hours != null && hours > 0
    ? (hours % 1 === 0 ? String(hours) : hours.toFixed(2))
    : null;

  if (editing) {
    return (
      <td className="px-1 py-1 text-center">
        <input
          ref={inputRef}
          type="number"
          min="0"
          max="24"
          step="0.25"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-12 text-center text-xs bg-white/10 border border-purple-500/60 rounded px-1 py-0.5 text-white outline-none focus:border-purple-400"
          autoFocus
        />
      </td>
    );
  }

  return (
    <td
      className={`px-1 py-1 text-center text-xs transition-colors ${
        readOnly
          ? "text-white/40 cursor-default"
          : "text-white/70 cursor-pointer hover:bg-white/5 hover:text-white rounded"
      } ${saving ? "opacity-50" : ""}`}
      onClick={handleClick}
      title={readOnly ? undefined : "Click to edit"}
    >
      {displayValue ?? <span className="text-white/20">—</span>}
    </td>
  );
}
